import { createMemo, onCleanup, onMount, type Accessor } from "solid-js"
import { createStore } from "solid-js/store"
import { createSimpleContext } from "@mux/ui/context"
import { useDialog } from "@mux/ui/context/dialog"
import { dict as en } from "@/i18n/en"
import { useLanguage } from "@/context/language"
import { useSettings } from "@/context/settings"
import { defaultShortcut } from "@/utils/keyboard-shortcuts"

const IS_MAC = typeof navigator === "object" && /(Mac|iPod|iPhone|iPad)/.test(navigator.platform)

const SUGGESTED_PREFIX = "suggested."
const EDITABLE_KEYBIND_IDS = new Set(["terminal.toggle", "file.attach", "prompt.interrupt"])
const BROWSER_RESERVED_KEYBIND_IDS = new Set(["session.undo", "session.redo"])
// Holding these must not cascade through the next chats or windows.
const UNREPEATED_KEYBIND_IDS = new Set(["session.archive", "grid.window.close"])

type KeyLabel =
  | "common.key.ctrl"
  | "common.key.alt"
  | "common.key.shift"
  | "common.key.meta"
  | "common.key.space"
  | "common.key.backspace"
  | "common.key.enter"
  | "common.key.tab"
  | "common.key.delete"
  | "common.key.home"
  | "common.key.end"
  | "common.key.pageUp"
  | "common.key.pageDown"
  | "common.key.insert"
  | "common.key.esc"

function keyText(key: KeyLabel, t?: (key: KeyLabel) => string) {
  return t ? t(key) : en[key]
}

function actionId(id: string) {
  if (!id.startsWith(SUGGESTED_PREFIX)) return id
  return id.slice(SUGGESTED_PREFIX.length)
}

function normalizeKey(key: string | undefined) {
  if (!key) return ""
  if (key === ",") return "comma"
  if (key === "+") return "plus"
  if (key === "-" || key === "_") return "minus"
  if (key === " ") return "space"
  return key.toLowerCase()
}

function normalizeEventKey(event: KeyboardEvent) {
  if (/^Digit[0-9]$/.test(event.code)) return event.code.slice(-1)
  return normalizeKey(event.key)
}

function signature(key: string, ctrl: boolean, meta: boolean, shift: boolean, alt: boolean) {
  const mask = (ctrl ? 1 : 0) | (meta ? 2 : 0) | (shift ? 4 : 0) | (alt ? 8 : 0)
  return `${key}:${mask}`
}

function signatureFromEvent(event: KeyboardEvent) {
  return signature(normalizeEventKey(event), event.ctrlKey, event.metaKey, event.shiftKey, event.altKey)
}

function isAllowedEditableKeybind(id: string | undefined) {
  if (!id) return false
  return EDITABLE_KEYBIND_IDS.has(actionId(id))
}

export type KeybindConfig = string

export interface Keybind {
  key: string
  ctrl: boolean
  meta: boolean
  shift: boolean
  alt: boolean
}

export interface CommandOption {
  id: string
  title: string
  description?: string
  category?: string
  keybind?: KeybindConfig
  /** The keybind is the user's own choice, so it outranks default shortcuts. */
  customKeybind?: boolean
  slash?: string
  suggested?: boolean
  disabled?: boolean
  onSelect?: (source?: "palette" | "keybind" | "slash") => void
  onHighlight?: () => (() => void) | void
}

type CommandSource = "palette" | "keybind" | "slash"

export type CommandRegistration = {
  key?: string
  options: Accessor<CommandOption[]>
}

function upsertCommandRegistration(registrations: CommandRegistration[], entry: CommandRegistration) {
  if (entry.key === undefined) return [entry, ...registrations]
  return [entry, ...registrations.filter((x) => x.key !== entry.key)]
}

export function parseKeybind(config: string): Keybind[] {
  if (!config || config === "none") return []

  return config.split(",").map((combo) => {
    const parts = combo.trim().toLowerCase().split("+")
    const keybind: Keybind = {
      key: "",
      ctrl: false,
      meta: false,
      shift: false,
      alt: false,
    }

    for (const part of parts) {
      switch (part) {
        case "ctrl":
        case "control":
          keybind.ctrl = true
          break
        case "meta":
        case "cmd":
        case "command":
          keybind.meta = true
          break
        case "mod":
          if (IS_MAC) keybind.meta = true
          else keybind.ctrl = true
          break
        case "alt":
        case "option":
          keybind.alt = true
          break
        case "shift":
          keybind.shift = true
          break
        default:
          keybind.key = part
          break
      }
    }

    return keybind
  })
}

export function matchKeybind(keybinds: Keybind[], event: KeyboardEvent): boolean {
  const eventKey = normalizeEventKey(event)

  for (const kb of keybinds) {
    const keyMatch = kb.key === eventKey
    const ctrlMatch = kb.ctrl === (event.ctrlKey || false)
    const metaMatch = kb.meta === (event.metaKey || false)
    const shiftMatch = kb.shift === (event.shiftKey || false)
    const altMatch = kb.alt === (event.altKey || false)

    if (keyMatch && ctrlMatch && metaMatch && shiftMatch && altMatch) {
      return true
    }
  }

  return false
}

export function formatKeybind(config: string, t?: (key: KeyLabel) => string): string {
  if (!config || config === "none") return ""

  const keybinds = parseKeybind(config)
  if (keybinds.length === 0) return ""

  return keybinds
    .map((kb) => {
      const parts: string[] = []

      if (kb.ctrl) parts.push(IS_MAC ? "⌃" : keyText("common.key.ctrl", t))
      if (kb.alt) parts.push(IS_MAC ? "⌥" : keyText("common.key.alt", t))
      if (kb.shift) parts.push(IS_MAC ? "⇧" : keyText("common.key.shift", t))
      if (kb.meta) parts.push(IS_MAC ? "⌘" : keyText("common.key.meta", t))

      if (kb.key) {
        const keys: Record<string, string> = {
          arrowup: "↑",
          arrowdown: "↓",
          arrowleft: "←",
          arrowright: "→",
          comma: ",",
          plus: "+",
          minus: "-",
        }
        const named: Record<string, KeyLabel> = {
          backspace: "common.key.backspace",
          delete: "common.key.delete",
          end: "common.key.end",
          enter: "common.key.enter",
          esc: "common.key.esc",
          escape: "common.key.esc",
          home: "common.key.home",
          insert: "common.key.insert",
          pagedown: "common.key.pageDown",
          pageup: "common.key.pageUp",
          space: "common.key.space",
          tab: "common.key.tab",
        }
        const key = kb.key.toLowerCase()
        const displayKey =
          keys[key] ??
          (named[key]
            ? keyText(named[key], t)
            : key.length === 1
              ? key.toUpperCase()
              : key.charAt(0).toUpperCase() + key.slice(1))
        parts.push(displayKey)
      }

      return IS_MAC ? parts.join("") : parts.join("+")
    })
    .join(" / ")
}

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  if (target.closest("[contenteditable='true']")) return true
  if (target.closest("input, textarea, select")) return true
  return false
}

function hasCopySelection(event: KeyboardEvent) {
  if (event.key.toLowerCase() !== "c" || !(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return false
  const target = event.target
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
    if (target.selectionStart !== null && target.selectionEnd !== null && target.selectionStart !== target.selectionEnd) return true
  }
  return (window.getSelection()?.toString().length ?? 0) > 0
}

export const { use: useCommand, provider: CommandProvider } = createSimpleContext({
  name: "Command",
  init: () => {
    const dialog = useDialog()
    const settings = useSettings()
    const language = useLanguage()
    const [store, setStore] = createStore({
      registrations: [] as CommandRegistration[],
      suspendCount: 0,
    })
    const warnedDuplicates = new Set<string>()

    const bind = (option: CommandOption) => {
      const base = actionId(option.id)
      if (BROWSER_RESERVED_KEYBIND_IDS.has(base)) return
      const custom = settings.keybinds.get(base)
      const config = custom ?? defaultShortcut(base) ?? option.keybind
      if (!config || config === "none") return
      return config
    }

    const registered = createMemo(() => {
      const seen = new Set<string>()
      const all: CommandOption[] = []

      for (const reg of store.registrations) {
        for (const opt of reg.options()) {
          if (seen.has(opt.id)) {
            if (import.meta.env.DEV && !warnedDuplicates.has(opt.id)) {
              warnedDuplicates.add(opt.id)
              console.warn(`[command] duplicate command id \"${opt.id}\" registered; keeping first entry`)
            }
            continue
          }
          seen.add(opt.id)
          all.push(opt)
        }
      }

      return all
    })

    const options = createMemo(() => {
      const resolved = registered().map((opt) => ({
        ...opt,
        keybind: bind(opt),
      }))

      const suggested = resolved.filter((x) => x.suggested && !x.disabled)

      return [
        ...suggested.map((x) => ({
          ...x,
          id: SUGGESTED_PREFIX + x.id,
          category: language.t("command.category.suggested"),
        })),
        ...resolved,
      ]
    })

    const suspended = () => store.suspendCount > 0

    const keymap = createMemo(() => {
      const map = new Map<string, CommandOption>()
      const bound = options().filter((option) => !option.id.startsWith(SUGGESTED_PREFIX) && !option.disabled && option.keybind)
      // A shortcut the user chose outranks another command's default for the
      // same keys, such as a default added after the user picked those keys.
      const chosen = bound.filter((option) => option.customKeybind || settings.keybinds.get(actionId(option.id)) !== undefined)
      for (const option of [...chosen, ...bound]) {
        for (const kb of parseKeybind(option.keybind!)) {
          if (!kb.key) continue
          const sig = signature(kb.key, kb.ctrl, kb.meta, kb.shift, kb.alt)
          if (!map.has(sig)) map.set(sig, option)
        }
      }
      return map
    })

    const optionMap = createMemo(() => {
      const map = new Map<string, CommandOption>()
      for (const option of options()) {
        map.set(option.id, option)
        map.set(actionId(option.id), option)
      }
      return map
    })

    const run = (id: string, source?: CommandSource) => {
      const option = optionMap().get(id)
      option?.onSelect?.(source)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || suspended() || dialog.active) return

      const sig = signatureFromEvent(event)
      const option = keymap().get(sig)
      const modified = event.ctrlKey || event.metaKey || event.altKey
      const isTab = event.key === "Tab"

      if (isEditableTarget(event.target) && !isAllowedEditableKeybind(option?.id) && !modified && !isTab) return

      if (!option) return
      if (actionId(option.id) === "prompt.interrupt" && (event.repeat || event.isComposing || hasCopySelection(event))) return
      if (actionId(option.id) === "session.archive" && event.isComposing) return
      event.preventDefault()
      if (UNREPEATED_KEYBIND_IDS.has(actionId(option.id)) && event.repeat) return
      option.onSelect?.("keybind")
    }

    onMount(() => {
      // Solid delegates local handlers to document. Bubble after them so local
      // dismissal/terminal handling wins regardless of listener registration order.
      window.addEventListener("keydown", handleKeyDown)
    })

    onCleanup(() => {
      window.removeEventListener("keydown", handleKeyDown)
    })

    function register(cb: () => CommandOption[]): VoidFunction
    function register(key: string, cb: () => CommandOption[]): VoidFunction
    function register(key: string | (() => CommandOption[]), cb?: () => CommandOption[]) {
      const id = typeof key === "string" ? key : undefined
      const next = typeof key === "function" ? key : cb
      if (!next) return
      const options = createMemo(next)
      const entry: CommandRegistration = {
        key: id,
        options,
      }
      setStore("registrations", (arr) => upsertCommandRegistration(arr, entry))
      let active = true
      const dispose = () => {
        if (!active) return
        active = false
        setStore("registrations", (arr) => arr.filter((x) => x !== entry))
      }
      onCleanup(dispose)
      return dispose
    }

    return {
      register,
      trigger(id: string, source?: CommandSource) {
        run(id, source)
      },
      /** The command's shortcut for display, without keys another command owns. */
      keybind(id: string) {
        const base = actionId(id)
        const option = options().find((x) => actionId(x.id) === base)
        if (!option?.keybind) return ""
        const owned = option.keybind.split(",").filter((combo) => {
          const [kb] = parseKeybind(combo)
          if (!kb?.key) return true
          const owner = keymap().get(signature(kb.key, kb.ctrl, kb.meta, kb.shift, kb.alt))
          return !owner || actionId(owner.id) === base
        })
        return formatKeybind(owned.join(","), language.t)
      },
      keybinds(enabled: boolean) {
        setStore("suspendCount", (count) => Math.max(0, count + (enabled ? -1 : 1)))
      },
      suspended,
      get options() {
        return options()
      },
    }
  },
})
